/**
 * A message the user sent while a turn was running (a steer) is a `data-steer` part of that
 * turn's assistant message, written into its stream when the agent accepted it: its place among
 * the parts is the moment it was sent. Wherever the conversation is read as messages (its view,
 * the transcript a fresh agent session replays), the assistant message is split around it.
 */
export const STEER_PART_TYPE = 'data-steer';

export type SteerData = { messageId: string; text: string };

type MessageLike = {
  id: string;
  role: string;
  parts: readonly { type: string; data?: unknown }[];
  metadata?: unknown;
};

function steerOf(part: { type: string; data?: unknown }): SteerData | null {
  if (part.type !== STEER_PART_TYPE) return null;
  const data = part.data as Partial<SteerData> | undefined;
  return typeof data?.messageId === 'string' && typeof data.text === 'string'
    ? { messageId: data.messageId, text: data.text }
    : null;
}

/** Whether a message is a steer `steersAsMessages` made a message of. */
export function isSteerMessage(message: { metadata?: unknown }): boolean {
  return (message.metadata as { steer?: unknown } | undefined)?.steer === true;
}

/**
 * The messages with each steer as a user message of its own, between what the agent wrote
 * before and after it. The last piece of a split message keeps its id and metadata (the
 * streaming message, its usage and checkpoint); the earlier ones take `<id>~<n>`.
 */
export function steersAsMessages<T extends MessageLike>(messages: readonly T[]): T[] {
  const result: T[] = [];
  for (const message of messages) {
    if (message.role !== 'assistant' || !message.parts.some((part) => steerOf(part) !== null)) {
      result.push(message);
      continue;
    }
    let pieces = 0;
    let current: T['parts'][number][] = [];
    for (const part of message.parts) {
      const steer = steerOf(part);
      if (steer === null) {
        current.push(part);
        continue;
      }
      if (current.length > 0) {
        result.push({
          ...message,
          id: `${message.id}~${pieces}`,
          parts: current,
          metadata: undefined,
        });
        pieces += 1;
      }
      current = [];
      result.push({
        id: steer.messageId,
        role: 'user',
        parts: [{ type: 'text', text: steer.text }],
        metadata: { steer: true },
      } as unknown as T);
    }
    result.push({ ...message, parts: current });
  }
  return result;
}

/** The steers a list of messages holds, by the id their messages had. */
export function steerMessageIds(messages: readonly MessageLike[]): Set<string> {
  const ids = new Set<string>();
  for (const message of messages)
    for (const part of message.parts) {
      const steer = steerOf(part);
      if (steer !== null) ids.add(steer.messageId);
    }
  return ids;
}
